import React, { useState, useEffect } from 'react'
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Chip,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
  TablePagination,
} from '@mui/material'
import {
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  InfoOutlined as InfoIcon,
  Clear as ClearIcon,
  FileUpload as FileUploadIcon,
} from '@mui/icons-material'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import BillingForm from './components/BillingForm/BillingForm'
import RemarkModal from './components/RemarkModal/RemarkModal'
import ImportBillingRecordsModal from './components/ImportBillingRecordsModal/ImportBillingRecordsModal'
import './billing-records.scss'
import { fetchBillingRecords } from '../../services/billingRecords.service'
import usePermission from '../../hooks/usePermission'
import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import BillingRecordsMobileView from './components/BillingRecordsMobileView'

const BillingRecords = () => {
  const [records, setRecords] = useState([])
  const [loading, setLoading] = useState(true)
  const [filters, setFilters] = useState({
    plantName: '',
    organization: '',
    status: '',
    date: null,
  })
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [isRemarkOpen, setIsRemarkOpen] = useState(false)
  const [selectedRecord, setSelectedRecord] = useState(null)
  const [formMode, setFormMode] = useState('add') // 'add' or 'edit'
  const [screenHeight, setScreenHeight] = useState(window.innerHeight)
  const headerRef = React.useRef(null)
  const [headerHeight, setHeaderHeight] = useState(200)
  const { can } = usePermission()

  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)

  const [importModalOpen, setImportModalOpen] = useState(false)
  const [importStatus, setImportStatus] = useState({
    open: false,
    message: '',
    severity: 'success',
    errors: [],
  })

  useEffect(() => {
    const handleResize = () => {
      setScreenHeight(window.innerHeight)
      if (headerRef.current) {
        setHeaderHeight(headerRef.current.offsetHeight)
      }
    }
    window.addEventListener('resize', handleResize)
    handleResize()
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  // Load billing records from API
  useEffect(() => {
    const loadBillingRecords = async () => {
      try {
        setLoading(true)
        const params = {
          skip: 0,
          limit: 1000, // Get all records for client-side filtering
        }

        const response = await fetchBillingRecords(params)

        if (response.success) {
          // Map API data to match the existing UI structure
          const mappedRecords = response.data.map(record => ({
            id: record.id,
            plantId: record.plantId,
            plantName: record.plantName,
            recordDate: record.recordDate,
            date: record.invoiceDate,
            dueDate: record.dueDate,
            capacity: record.plantSize,
            organization: record.organizationName || 'N/A',
            organizationId: record.organizationId || 'N/A',
            billedFrom: record.billedFrom || 'N/A',
            actualGeneration: record.actualGeneration,
            deemedGeneration: record.deemedGeneration,
            tariff: record.tariff,
            amount: record.amount,
            currency: record.currency || 'INR',
            status: record.paymentStatus,
            type: record.type,
            invoice: record.invoiceNumber,
            remark: record.remarks,
            updatedAt: record.updatedAt,
          }))
          setRecords(mappedRecords)
        }
      } catch (error) {
        console.error('Error loading billing records:', error)
      } finally {
        setLoading(false)
      }
    }

    loadBillingRecords()
  }, [])

  const handleAddRecord = () => {
    setFormMode('add')
    setSelectedRecord(null)
    setIsFormOpen(true)
  }

  const handleEditRecord = (record) => {
    setFormMode('edit')
    setSelectedRecord(record)
    setIsFormOpen(true)
  }

  const handleViewRemark = (record) => {
    setSelectedRecord(record)
    setIsRemarkOpen(true)
  }

  const handleCloseForm = () => {
    setIsFormOpen(false)
    setSelectedRecord(null)
  }

  const handleCloseRemark = () => {
    setIsRemarkOpen(false)
    setSelectedRecord(null)
  }

  const handleOpenImportModal = () => {
    setImportModalOpen(true)
  }

  const handleCloseImportModal = () => {
    setImportModalOpen(false)
  }

  const handleImportSuccess = async (importResult) => {
    try {
      const created = importResult?.created || 0
      const skipped = importResult?.skipped || 0
      const errors = importResult?.errors || []
      const total = created + skipped

      const message = `Imported ${created} out of ${total} records.`
      const severity = errors && errors.length > 0 ? 'error' : skipped > 0 ? 'warning' : 'success'

      setImportStatus({ open: true, message, severity, errors })
    } catch (err) {
      console.error('Error preparing import notification:', err)
    }

    // Refresh the records list after successful import
    try {
      setLoading(true)
      const params = { skip: 0, limit: 1000 }
      const response = await fetchBillingRecords(params)

      if (response.success) {
        const mappedRecords = response.data.map(record => ({
          id: record.id,
          plantId: record.plantId,
          plantName: record.plantName,
          recordDate: record.recordDate,
          date: record.invoiceDate,
          dueDate: record.dueDate,
          capacity: record.plantSize,
          organization: record.organizationName || 'N/A',
          organizationId: record.organizationId || 'N/A',
          billedFrom: record.billedFrom || 'N/A',
          actualGeneration: record.actualGeneration,
          deemedGeneration: record.deemedGeneration,
          tariff: record.tariff,
          amount: record.amount,
          currency: record.currency || 'INR',
          status: record.paymentStatus,
          type: record.type,
          invoice: record.invoiceNumber,
          remark: record.remarks,
          updatedAt: record.updatedAt,
        }))
        setRecords(mappedRecords)
      }
    } catch (error) {
      console.error('Error refreshing records:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSaveRecord = async (recordData) => {
    // Refresh the billing records list after save
    try {
      const params = {
        skip: 0,
        limit: 1000,
      }

      const response = await fetchBillingRecords(params)

      if (response.success) {
        const mappedRecords = response.data.map(record => ({
          id: record.id,
          plantId: record.plantId,
          plantName: record.plantName,
          recordDate: record.recordDate,
          date: record.invoiceDate,
          dueDate: record.dueDate,
          capacity: record.plantSize,
          organization: record.organizationName || 'N/A',
          organizationId: record.organizationId || 'N/A',
          billedFrom: record.billedFrom || 'N/A',
          actualGeneration: record.actualGeneration,
          deemedGeneration: record.deemedGeneration,
          tariff: record.tariff,
          amount: record.amount,
          currency: record.currency || 'INR',
          status: record.paymentStatus,
          type: record.type,
          invoice: record.invoiceNumber,
          remark: record.remarks,
          updatedAt: record.updatedAt,
        }))
        setRecords(mappedRecords)
      }
    } catch (error) {
      console.error('Error refreshing billing records:', error)
    }
    handleCloseForm()
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'Paid':
        return 'success'
      case 'Pending':
        return 'warning'
      case 'Unpaid':
        return 'error'
      case 'To Be Raised':
        return 'default'
      case 'Draft':
        return 'default'
      default:
        return 'default'
    }
  }

  const filteredRecords = records.filter((record) => {
    if (filters.plantName && record.plantName !== filters.plantName)
      return false
    if (filters.organization && record.organization !== filters.organization)
      return false
    if (filters.status && record.status !== filters.status) return false
    if (
      filters.date &&
      dayjs(record.recordDate).format('MM-YYYY') !== dayjs(filters.date).format('MM-YYYY')
    )
      return false
    return true
  })

  const plantNames = [...new Set(records.map((r) => r.plantName))]
  const organizations = [...new Set(records.map((r) => r.organization))]
  const statuses = ['Paid', 'Unpaid', 'Pending', 'To Be Raised', 'Draft']

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  if (isMobile) {
    return (
      <BillingRecordsMobileView
        records={filteredRecords}
        loading={loading}
        filters={filters}
        setFilters={setFilters}
        plantNames={plantNames}
        organizations={organizations}
        statuses={statuses}
        handleAddRecord={handleAddRecord}
        handleEditRecord={handleEditRecord}
        handleViewRemark={handleViewRemark}
        handleOpenImportModal={handleOpenImportModal}
        isFormOpen={isFormOpen}
        handleCloseForm={handleCloseForm}
        handleSaveRecord={handleSaveRecord}
        selectedRecord={selectedRecord}
        formMode={formMode}
        isRemarkOpen={isRemarkOpen}
        handleCloseRemark={handleCloseRemark}
        importModalOpen={importModalOpen}
        handleCloseImportModal={handleCloseImportModal}
        handleImportSuccess={handleImportSuccess}
        importStatus={importStatus}
        setImportStatus={setImportStatus}
      />
    )
  }

  return (
    <div className="billing-records">


      <div className="billing-records-header" ref={headerRef}>
        <Typography variant="h4" component="h1" className="page-title">
          Billing Records
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {can('billing.create') && (
            <Button
              variant="contained"
              startIcon={<FileUploadIcon />}
              onClick={handleOpenImportModal}
              className="import-record-btn"
              sx={{
                backgroundColor: '#5968b7',
                '&:hover': {
                  backgroundColor: '#4a5a9e',
                },
              }}
            >
              Import
            </Button>
          )}
          {can('billing.create') && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleAddRecord}
              className="add-record-btn"
            >
              Add Record
            </Button>
          )}
        </Box>
      </div>

      {loading ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: `${screenHeight - headerHeight}px`,
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Filters */}
          <Card className="filters-card" sx={{ mt: 2 }}>
            <CardContent>
              <Box sx={{ width: '100%', overflowX: 'auto' }}>
                <Box
                  sx={{
                    display: 'flex',
                    gap: 2,
                    alignItems: 'center',
                    minWidth: '800px',
                    pt: 1,
                  }}
                >
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Plant Name</InputLabel>
                    <Select
                      value={filters.plantName}
                      onChange={(e) =>
                        setFilters({ ...filters, plantName: e.target.value })
                      }
                      label="Plant Name"
                    >
                      <MenuItem value="">All</MenuItem>
                      {plantNames.map((name) => (
                        <MenuItem key={name} value={name}>
                          {name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Organization</InputLabel>
                    <Select
                      value={filters.organization}
                      onChange={(e) =>
                        setFilters({ ...filters, organization: e.target.value })
                      }
                      label="Organization"
                    >
                      <MenuItem value="">All</MenuItem>
                      {organizations.map((org) => (
                        <MenuItem key={org} value={org}>
                          {org}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={filters.status}
                      onChange={(e) =>
                        setFilters({ ...filters, status: e.target.value })
                      }
                      label="Status"
                    >
                      <MenuItem value="">All</MenuItem>
                      {statuses.map((status) => (
                        <MenuItem key={status} value={status}>
                          {status}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minHeight: 40 }}>
                      <DatePicker
                        label="Date"
                        views={['month', 'year']}
                        value={filters.date}
                        onChange={(newValue) =>
                          setFilters({ ...filters, date: newValue })
                        }
                        slotProps={{
                          textField: {
                            size: 'small',
                            sx: { minWidth: 180 },
                          },
                        }}
                      />
                      {filters.date && (
                        <IconButton
                          size="small"
                          onClick={() => setFilters({ ...filters, date: null })}
                          title="Clear date filter"
                          sx={{ ml: -0.5 }}
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </LocalizationProvider>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Table */}
          <Card className="billing-table-card" sx={{ mt: 2 }}>
            <CardContent>
              <Box sx={{ width: '100%', overflowX: 'auto' }}>
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Plant Name</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Capacity (kWp)</TableCell>
                        <TableCell>Organization</TableCell>
                        <TableCell>Billed From</TableCell>
                        <TableCell>Actual Generation (kWh)</TableCell>
                        <TableCell>Deemed Generation (kWh)</TableCell>
                        <TableCell>Tariff</TableCell>
                        <TableCell>Amount</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Invoice</TableCell>
                        <TableCell>Remark</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredRecords
                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        .map((record, index) => (
                          <TableRow key={record.id} hover>
                            <TableCell>{record.plantName || "N/A"}</TableCell>
                            <TableCell>
                              {record.recordDate ? dayjs(record.recordDate).format('MM-YYYY') : 'N/A'}
                            </TableCell>
                            <TableCell>{record.capacity || "N/A"}</TableCell>
                            <TableCell>
                              <Box display="flex" alignItems="center" gap={1}>
                                {record.organization}
                                <Tooltip title={`Organisation ID: ${record.organizationId}`}>
                                  <InfoIcon fontSize="small" color="action" />
                                </Tooltip>
                              </Box>
                            </TableCell>
                            <TableCell>{record.billedFrom || "N/A"}</TableCell>
                            <TableCell>{record.actualGeneration || "N/A"}</TableCell>
                            <TableCell>{record.deemedGeneration || "0"}</TableCell>
                            <TableCell>{record.tariff || "N/A"}</TableCell>
                            <TableCell>
                              <Box display="flex" alignItems="center" gap={1}>
                                {record.amount || "N/A"}
                                <Tooltip title={`Currency: ${record.currency || 'INR'}`}>
                                  <InfoIcon fontSize="small" sx={{ color: 'warning.main' }} />
                                </Tooltip>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={record.status}
                                color={getStatusColor(record.status)}
                                size="small"
                              />
                            </TableCell>
                            <TableCell
                              style={{
                                color:
                                  record.status === 'To Be Raised'
                                    ? '#ccc'
                                    : 'inherit',
                              }}
                            >
                              {record.status === 'To Be Raised'
                                ? '-'
                                : record.invoice}
                            </TableCell>
                            <TableCell>
                              {record.status === 'To Be Raised' ? (
                                '-'
                              ) : (
                                <Box display="flex" alignItems="center" gap={0.5}>
                                  {(record.status === 'Pending' ||
                                    record.status === 'Unpaid' ||
                                    record.status === 'Paid' ||
                                    record.status === 'Draft') && (
                                      <IconButton
                                        size="small"
                                        onClick={() => handleViewRemark(record)}
                                        title="View Remark"
                                      >
                                        <ViewIcon fontSize="small" />
                                      </IconButton>
                                    )}
                                </Box>
                              )}
                            </TableCell>
                            <TableCell>
                              {can('billing.update') && (record.status === 'To Be Verified' || record.status === 'To Be Raised') && (
                                <Tooltip title="Edit Record">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleEditRecord(record)}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      {filteredRecords.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={13} align="center">
                            <Typography variant="body2" color="textSecondary">
                              No records found
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  component="div"
                  count={filteredRecords.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={(event, newPage) => setPage(newPage)}
                  onRowsPerPageChange={(event) => {
                    setRowsPerPage(parseInt(event.target.value, 10))
                    setPage(0)
                  }}
                  sx={{
                    borderTop: '1px solid #e0e0e0',
                    '& .MuiTablePagination-toolbar': {
                      px: { xs: 1, sm: 2 },
                      minHeight: '52px',
                    },
                    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                      fontSize: '0.875rem',
                      color: 'text.secondary',
                      mb: 0,
                    },
                    '& .MuiTablePagination-select': {
                      fontSize: '0.875rem',
                    },
                  }}
                />
              </Box>
            </CardContent>
          </Card>

          {/* Billing Form Dialog */}
          <BillingForm
            open={isFormOpen}
            onClose={handleCloseForm}
            onSave={handleSaveRecord}
            record={selectedRecord}
            mode={formMode}
          />

          {/* Remark Modal */}
          <RemarkModal
            open={isRemarkOpen}
            onClose={handleCloseRemark}
            record={selectedRecord}
          />

          {/* Import Modal */}
          <ImportBillingRecordsModal
            open={importModalOpen}
            onClose={handleCloseImportModal}
            onImportSuccess={handleImportSuccess}
          />

          <Snackbar
            open={importStatus.open}
            autoHideDuration={10000}
            onClose={() => setImportStatus({ ...importStatus, open: false })}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <Alert
              onClose={() => setImportStatus({ ...importStatus, open: false })}
              severity={importStatus.severity}
              sx={{ width: '100%' }}
              variant="filled"
            >
              <div>{importStatus.message}</div>
              {importStatus.errors && importStatus.errors.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  {importStatus.errors.slice(0, 5).map((e, i) => (
                    <div key={i} style={{ fontSize: 12 }}>
                      {`Row ${e.row}${e.plantName ? ` (${e.plantName})` : ''}: ${e.reason}`}
                    </div>
                  ))}
                  {importStatus.errors.length > 5 && (
                    <div style={{ fontSize: 12 }}>...and {importStatus.errors.length - 5} more</div>
                  )}
                </div>
              )}
            </Alert>
          </Snackbar>
        </>
      )}
    </div>
  )
}

export default BillingRecords
